import { useForm } from "react-hook-form";
import { type FocusEvent, type ChangeEvent, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { axios, type AxiosResponse } from "@root/libs/axios";
import {
  type FormValues,
  schema,
} from "@root/userFrontend/features/registration/schemas/step1/schema";
import { isString } from "@root/shared/utils/typeGuard";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";

type UseStep1FormProps = {
  mode?: "onBlur" | "onChange";
  emailDuplicateCheckPath: string;
  defaultEmailErrorMessages?: string[];
  defaultPasswordErrorMessages?: string[];
};

export const useStep1Form = ({
  emailDuplicateCheckPath,
  defaultEmailErrorMessages,
  defaultPasswordErrorMessages,
  mode = "onBlur",
}: UseStep1FormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
  } = useForm<FormValues>({
    mode,
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const formRef = useRef<HTMLFormElement>(null);
  const emailValue = watch("email");
  const passwordValue = watch("password");

  const [emailErrorMessages, setEmailErrorMessages] = useState<string[]>(
    defaultEmailErrorMessages ?? [],
  );
  const [passwordErrorMessages, setPasswordErrorMessages] = useState<string[]>(
    defaultPasswordErrorMessages ?? [],
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const lastEmailValueRef = useRef<string>("");

  const getEmailDuplicateMessage = async (fieldValue: string) => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    lastEmailValueRef.current = fieldValue;

    try {
      const res = await axios.post<
        { fieldValue: string },
        AxiosResponse<{ status: boolean; msg: string | null }>
      >(
        emailDuplicateCheckPath,
        { fieldValue },
        { signal: abortController.signal, caseConversion: "none" },
      );

      return !abortController.signal.aborted ? res.data.msg : null;
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        sendErrorLog(error);
      }
      return null;
    }
  };

  const validateEmail = async (value: string) => {
    setEmailErrorMessages([]);

    // エラーがある場合、値が変わっていない場合は何もしない
    if (errors.email || lastEmailValueRef.current === value) return;
    // 既存のリクエストをキャンセル
    if (abortControllerRef.current) abortControllerRef.current.abort();

    const msg = await getEmailDuplicateMessage(value);
    if (msg) setEmailErrorMessages([msg]);
  };

  const handleEmailBlur = (e: FocusEvent<HTMLInputElement>) => {
    register("email").onBlur(e);
    validateEmail(e.target.value);
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    register("email").onChange(e);
    validateEmail(e.target.value);
  };

  const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    register("password").onBlur(e);
    setPasswordErrorMessages([]);
  };
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    register("password").onChange(e);
    setPasswordErrorMessages([]);
  };

  const handleSubmitButtonClick = handleSubmit(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    formRef.current?.submit();
  });

  const isSubmitButtonDisabled =
    !touchedFields.email ||
    !touchedFields.password ||
    !!errors.email ||
    !!errors.password ||
    emailErrorMessages.length > 0 ||
    passwordErrorMessages.length > 0 ||
    !emailValue ||
    !passwordValue;

  return {
    formRef,
    email: {
      register: {
        ...register("email"),
        ...(mode === "onBlur" && { onBlur: handleEmailBlur }),
        ...(mode === "onChange" && { onChange: handleEmailChange }),
      },
      value: emailValue,
      errorMessages: [errors.email?.message, ...emailErrorMessages].filter(isString),
    },
    password: {
      register: {
        ...register("password"),
        ...(mode === "onBlur" && { onBlur: handlePasswordBlur }),
        ...(mode === "onChange" && { onChange: handlePasswordChange }),
      },
      value: passwordValue,
      errorMessages: [errors.password?.message, ...passwordErrorMessages].filter(isString),
    },
    handleSubmitButtonClick,
    isSubmitButtonDisabled,
  };
};
